import React, { useState } from "react";
import { Card, CardHeader, CardBody, Table, Badge } from "reactstrap";
import Loader from "components/Loader.js";

const RevenueByDomainChart = ({ data, loading }) => {
    const adUnits = data?.revenue?.byAdUnit || [];
    const [sortColumn, setSortColumn] = useState('revenue');
    const [sortDirection, setSortDirection] = useState('desc');

    // Calculate total revenue and percentages
    const totalRevenue = adUnits.reduce((sum, item) => sum + item.revenue, 0);

    const adUnitsWithPercentage = adUnits.map(item => ({
        ...item,
        percentage: totalRevenue > 0 ? (item.revenue / totalRevenue * 100) : 0
    }));

    // Sorting function
    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('desc');
        }
    };

    // Sort data
    const sortedData = [...adUnitsWithPercentage].sort((a, b) => {
        let valA, valB;

        if (sortColumn === 'adUnit') {
            valA = a.adUnit.toLowerCase();
            valB = b.adUnit.toLowerCase();
            return sortDirection === 'asc'
                ? valA.localeCompare(valB)
                : valB.localeCompare(valA);
        } else {
            valA = a[sortColumn];
            valB = b[sortColumn];
            return sortDirection === 'asc' ? valA - valB : valB - valA;
        }
    });

    const colors = [
        '#5e72e4',
        '#2dce89',
        '#11cdef',
        '#f5365c',
        '#fb6340',
        '#ffd600',
        '#8965e0',
        '#f7fafc',
        '#172b4d',
        '#32325d'
    ];

    const getSortIcon = (column) => {
        if (sortColumn !== column) return '⇅';
        return sortDirection === 'asc' ? '▲' : '▼';
    };

    return (
        <Card className="shadow" style={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
            <CardHeader className="bg-transparent">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h6 className="text-uppercase text-muted ls-1 mb-1">Receita</h6>
                        <h2 className="mb-0">Receita por Domínio</h2>
                    </div>
                    <div className="text-right">
                        <h6 className="text-uppercase text-muted ls-1 mb-1">Total Hoje</h6>
                        <h2 className="text-success mb-0">${totalRevenue.toFixed(2)}</h2>
                    </div>
                </div>
            </CardHeader>
            <CardBody style={{ flex: 1, overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' }}>
                {loading ? (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Loader variant="card" size="md" />
                    </div>
                ) : adUnits.length === 0 ? (
                    <div className="text-center p-5 text-muted">
                        <i className="ni ni-money-coins fa-3x mb-3"></i>
                        <p>Nenhum dado de receita disponível</p>
                    </div>
                ) : (
                    <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
                        <Table className="align-items-center table-flush" style={{ tableLayout: 'fixed', width: '100%' }}>
                            <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                <tr>
                                    <th
                                        scope="col"
                                        style={{ cursor: 'pointer', width: '45%' }}
                                        onClick={() => handleSort('adUnit')}
                                    >
                                        Domínio {getSortIcon('adUnit')}
                                    </th>
                                    <th
                                        scope="col"
                                        className="text-right"
                                        style={{ cursor: 'pointer', width: '30%' }}
                                        onClick={() => handleSort('revenue')}
                                    >
                                        Receita {getSortIcon('revenue')}
                                    </th>
                                    <th
                                        scope="col"
                                        className="text-right"
                                        style={{ cursor: 'pointer', width: '25%' }}
                                        onClick={() => handleSort('percentage')}
                                    >
                                        % {getSortIcon('percentage')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.map((item, index) => (
                                    <tr key={index}>
                                        <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            <div className="d-flex align-items-center">
                                                <span
                                                    className="mr-2"
                                                    style={{
                                                        display: 'inline-block',
                                                        width: '12px',
                                                        height: '12px',
                                                        borderRadius: '50%',
                                                        backgroundColor: colors[index % colors.length],
                                                        flexShrink: 0
                                                    }}
                                                ></span>
                                                <span className="text-sm font-weight-bold" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {item.adUnit}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="text-right">
                                            <span className="font-weight-bold text-success">
                                                ${item.revenue.toFixed(2)}
                                            </span>
                                        </td>
                                        <td className="text-right">
                                            <Badge color="info" pill>
                                                {item.percentage.toFixed(1)}%
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default RevenueByDomainChart;
